import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { ToastInterface, useToast } from 'vue-toastification'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

import {
	CreateRatePlanCodeParams,
	RatePlanCode,
	UpdateRatePlanCodeParams,
} from '@/internal/modules/providers/interfaces/RatePlanCode'

interface Props {
	immediate?: boolean
	params?: Params
	config?: Config
}

interface Config {
	errorHandling: boolean
}

interface Params {
	hotelId?: string | number
	chainId?: string | number
}

interface DeleteParams {
	id: number
}

const getRatePlanCodes = async (
	params: Params,
	errorHandling = true
): Promise<RatePlanCode[]> => {
	const queryParams = new URLSearchParams()

	if (params.hotelId) {
		queryParams.append('hotelId', String(params.hotelId))
	}

	const { data } = await axios.get<RatePlanCode[]>(
		`api/internal/hotels/${params.hotelId}/rate-plan-codes`,
		{
			errorHandling,
		} as CustomAxiosRequestConfig
	)

	return data
}

const createRatePlanCode = async (
	code: CreateRatePlanCodeParams,
	toast: ToastInterface
) => {
	try {
		const { data } = await axios.post<RatePlanCode>(
			`/api/internal/hotels/${code.hotelId}/rate-plan-codes`,
			{
				...code,
				gds: 'amadeus',
				serviceType: 'hotel',
			}
		)

		return data
	} catch (error: any) {
		toast.error(error.response?.data?.message || 'Error al crear el código')
		throw error
	}
}

const useHotelRatePlanCodes = (props: Props = {}) => {
	const toast = useToast()

	const { config, params } = props
	const { errorHandling = true } = config || {}
	const hotelId = params?.hotelId

	const { data, isLoading, isError, refetch, isRefetching } = useQuery(
		['rate-plan-codes', hotelId, params?.chainId],
		() =>
			getRatePlanCodes(
				{
					hotelId,
					chainId: params?.chainId,
				},
				errorHandling
			),
		{ enabled: props.immediate && (!!hotelId || !!params?.chainId) }
	)

	const createRatePlanCodeMutation = useMutation(
		(code: CreateRatePlanCodeParams) => createRatePlanCode(code, toast)
	)

	return {
		ratePlanCodes: data,
		isLoading,
		isRefetching,
		isError,
		refetch,

		createRatePlanCode: createRatePlanCodeMutation.mutate,
		createRatePlanCodeAsync: createRatePlanCodeMutation.mutateAsync,
		createRatePlanCodeLoading: createRatePlanCodeMutation.isLoading,
	}
}

export default useHotelRatePlanCodes
